import { useMemo } from 'react'
import { collection, query, where } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { isGroupAdmin } from '@shared/groupRoles'
import { useBatchedSubscriptions } from './useBatchedSubscriptions'
import { useGroups } from './useGroups'
import { byCreatedAt } from './useGroupPendingRequests'
import type { Group, GroupEventRequest } from '../types'

export interface AdminGroupSuggestion {
  request: GroupEventRequest
  group: Group
}

/**
 * Suggestions waiting on the viewer across every group they admin — Crew's
 * "Waiting on you" and its nav badge. Drops suggestions whose event is already
 * linked or whose suggester left, like the group page does.
 */
export function useAdminGroupSuggestions(userId: string | null): { suggestions: AdminGroupSuggestion[]; loading: boolean } {
  const { groups } = useGroups(userId)
  const adminGroups = useMemo(() => groups.filter(g => isGroupAdmin(g, userId)), [groups, userId])
  const ids = useMemo(() => adminGroups.map(g => g.id), [adminGroups])

  const requests = useBatchedSubscriptions<GroupEventRequest>(
    ids,
    batch => query(collection(db, 'groupEventRequests'), where('groupId', 'in', batch), where('status', '==', 'pending')),
    d => ({ ...(d.data() as Omit<GroupEventRequest, 'id'>), id: d.id }),
    { queryKey: 'pending' },
  )
  // Only needed to hide suggestions an admin already linked directly, so it
  // listens to the groups that have one waiting.
  const waitingIds = useMemo(() => [...new Set(requests.rows.map(r => r.groupId))], [requests.rows])
  const links = useBatchedSubscriptions<{ groupId: string; eventId: string }>(
    waitingIds,
    batch => query(collection(db, 'groupEvents'), where('groupId', 'in', batch)),
    d => ({ groupId: d.get('groupId') as string, eventId: d.get('eventId') as string }),
  )

  const suggestions = useMemo(() => {
    const byId = new Map(adminGroups.map(g => [g.id, g]))
    const linked = new Set(links.rows.map(l => `${l.groupId}_${l.eventId}`))
    return [...requests.rows].sort(byCreatedAt).flatMap(request => {
      const group = byId.get(request.groupId)
      if (!group || !group.memberIds.includes(request.requestedBy)) return []
      return linked.has(`${request.groupId}_${request.eventId}`) ? [] : [{ request, group }]
    })
  }, [adminGroups, requests.rows, links.rows])

  return { suggestions, loading: requests.loading }
}
